import type { GrowBag, LossRecord, MasterMix, Order, PhotoLog, SpawnLot, Task } from '../types';
import { supabase } from './supabase';

export const OPERATIONAL_KEYS = {
  GROW_BAGS: 'grow_bags',
  MASTER_MIXES: 'master_mixes',
  SPAWN_LOTS: 'spawn_lots',
  LOSS_RECORDS: 'loss_records',
  PHOTO_LOGS: 'photo_logs',
  ORDERS: 'orders',
  TASKS: 'tasks',
} as const;

type OperationalKey = typeof OPERATIONAL_KEYS[keyof typeof OPERATIONAL_KEYS];

function now() {
  return new Date().toISOString();
}

function asDate(value?: string) {
  if (!value) return null;
  return value.slice(0, 10);
}

function photoDate(value: string) {
  return value.includes('T') ? value : `${value}T00:00:00.000Z`;
}

const mappers = {
  [OPERATIONAL_KEYS.MASTER_MIXES]: (mix: MasterMix) => ({
    id: mix.id,
    name: mix.name,
    species_id: mix.speciesId,
    total_weight_kg: mix.totalWeightKg,
    ingredients: mix.ingredients,
    notes: mix.notes ?? null,
    updated_at: now(),
  }),
  [OPERATIONAL_KEYS.SPAWN_LOTS]: (lot: SpawnLot) => ({
    id: lot.id,
    name: lot.name,
    species_id: lot.speciesId,
    source: lot.source,
    supplier: lot.supplier ?? null,
    production_date: asDate(lot.productionDate),
    total_grams: lot.totalGrams,
    notes: lot.notes ?? null,
    updated_at: now(),
  }),
  [OPERATIONAL_KEYS.GROW_BAGS]: (bag: GrowBag) => ({
    id: bag.id,
    name: bag.name,
    species_id: bag.speciesId,
    master_mix_id: bag.masterMixId ?? null,
    spawn_lot_id: bag.spawnLotId ?? null,
    inoculation_date: asDate(bag.inoculationDate),
    fruiting_start_date: asDate(bag.fruitingStartDate),
    harvest_date: asDate(bag.harvestDate),
    status: bag.status,
    position: bag.position,
    shelf: bag.shelf ?? null,
    bag_weight_grams: bag.bagWeightGrams ?? null,
    notes: bag.notes,
    flush_count: bag.flushCount,
    flush_harvests: bag.flushHarvests,
    total_yield_grams: bag.totalYieldGrams,
    placed_by: bag.placedBy ?? null,
    placed_at: bag.placedAt ?? null,
    last_seen_at: bag.lastSeenAt ?? null,
    updated_at: now(),
  }),
  [OPERATIONAL_KEYS.ORDERS]: (order: Order) => ({
    id: order.id,
    order_number: order.orderNumber,
    customer_name: order.customerName,
    channel: order.channel,
    order_date: asDate(order.orderDate),
    delivery_date: asDate(order.deliveryDate),
    items: order.items,
    status: order.status,
    notes: order.notes ?? null,
    updated_at: now(),
  }),
  [OPERATIONAL_KEYS.TASKS]: (task: Task) => ({
    id: task.id,
    title: task.title,
    category: task.category,
    due_date: asDate(task.dueDate),
    completed: task.completed,
    bag_id: task.bagId ?? null,
    priority: task.priority,
    notes: task.notes ?? null,
    auto_generated: task.autoGenerated ?? false,
    updated_at: now(),
  }),
  [OPERATIONAL_KEYS.LOSS_RECORDS]: (loss: LossRecord) => ({
    id: loss.id,
    bag_id: loss.bagId,
    bag_name: loss.bagName,
    date: asDate(loss.date),
    type: loss.type,
    loss_grams: loss.lossGrams ?? null,
    notes: loss.notes ?? null,
    updated_at: now(),
  }),
  [OPERATIONAL_KEYS.PHOTO_LOGS]: (photo: PhotoLog) => ({
    id: photo.id,
    bag_id: photo.bagId,
    date: photoDate(photo.date),
    description: photo.description,
    stage: photo.stage,
    image_emoji: photo.imageEmoji,
    image_url: photo.imageUrl ?? null,
    capture_source: photo.captureSource ?? 'manual',
    linked_event_id: photo.linkedEventId ?? null,
    updated_at: now(),
  }),
};

const tableByKey: Record<OperationalKey, string> = {
  [OPERATIONAL_KEYS.GROW_BAGS]: 'grow_bags',
  [OPERATIONAL_KEYS.MASTER_MIXES]: 'master_mixes',
  [OPERATIONAL_KEYS.SPAWN_LOTS]: 'spawn_lots',
  [OPERATIONAL_KEYS.LOSS_RECORDS]: 'loss_records',
  [OPERATIONAL_KEYS.PHOTO_LOGS]: 'photo_logs',
  [OPERATIONAL_KEYS.ORDERS]: 'orders',
  [OPERATIONAL_KEYS.TASKS]: 'tasks',
};

export async function mirrorOperationalState(key: string, value: unknown): Promise<void> {
  if (!supabase || !(key in tableByKey) || !Array.isArray(value)) return;
  const operationalKey = key as OperationalKey;
  const rows = value.map((item) => (mappers[operationalKey] as (v: any) => Record<string, unknown>)(item));
  const table = tableByKey[operationalKey];

  const { error } = await supabase
    .from(table)
    .upsert(rows, { onConflict: 'id' });
  if (error) throw error;
}

export async function recordOperationalEvent(input: {
  eventType: string;
  bagId?: string;
  bagName?: string;
  actor?: string;
  summary: string;
  payload?: Record<string, unknown>;
}): Promise<void> {
  if (!supabase) return;
  await supabase.from('operational_events').insert({
    id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    occurred_at: now(),
    event_type: input.eventType,
    bag_id: input.bagId ?? null,
    bag_name: input.bagName ?? null,
    actor: input.actor ?? 'web',
    summary: input.summary,
    payload: input.payload ?? {},
  });
}
